import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Image, Spin, Tooltip } from "antd";
import {
  DownOutlined,
  ExclamationCircleOutlined,
  LikeFilled,
  LikeOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import { observer } from "mobx-react-lite";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { ViewpointFeedApi } from "./ViewpointFeedApi";
import type { ViewpointFeedCategoryItem, ViewpointFeedPost } from "./ViewpointFeedTypes";
import "./ViewpointFeedPage.css";
import { globalModel } from "../../global/GlobalModel";
import { ViewpointReportModal } from "./ViewpointReportModal";
import { ViewpointHotBadge } from "./ViewpointHotBadge";
import { formatViewpointEngagementCount, buildViewpointDisplayItems, mergeFeedIncoming } from "./ViewpointUtil";
import { useConceptEntitlement } from "../../components/fin-web/ConceptEntitlementContext";
import { useConceptNavigate } from "../../components/fin-web/useConceptNavigate";
import { listViewpointConceptCatalog } from "./ViewpointConceptApi";
import {
  ViewpointConceptLinkedBody,
  ViewpointConceptTagRow,
} from "./ViewpointConceptContent";
import type { ConceptBrief } from "../concept/ConceptType";

const PAGE_SIZE = 8;
const POLL_MS = 5000;
/** 分类标签与自选股分组一致：超过该数量时收入「更多分类」 */
const FEED_CATEGORY_INLINE_MAX = 6;

function feedQueryExtras(categoryId: string | null) {
  return categoryId ? { categoryId } : {};
}

function normalizeFeedPost(p: ViewpointFeedPost): ViewpointFeedPost {
  return {
    ...p,
    likeCount: p.likeCount ?? 0,
    collectCount: p.collectCount ?? 0,
  };
}

function normalizePinnedPost(p: ViewpointFeedPost): ViewpointFeedPost {
  return { ...normalizeFeedPost(p), pinned: true };
}

function normalizeFeedOnlyPost(p: ViewpointFeedPost): ViewpointFeedPost {
  return { ...normalizeFeedPost(p), pinned: false };
}

function ViewpointFeedReportAction({
  row,
  onReport,
}: {
  row: ViewpointFeedPost;
  onReport: (e: React.MouseEvent, row: ViewpointFeedPost) => void;
}) {
  return (
    <div className="lf-vp-card__report">
      <Tooltip title="举报此内容">
        <button
          type="button"
          className="lf-vp-report-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            onReport(e, row);
          }}
          aria-label="举报此内容"
        >
          <ExclamationCircleOutlined className="lf-vp-report-action-ic" />
        </button>
      </Tooltip>
    </div>
  );
}

function ViewpointFeedEngagementActions({
  row,
  onToggleLike,
  onToggleCollect,
}: {
  row: ViewpointFeedPost;
  onToggleLike: (e: React.MouseEvent, row: ViewpointFeedPost) => void;
  onToggleCollect: (e: React.MouseEvent, row: ViewpointFeedPost) => void;
}) {
  const liked = Boolean(row.likedByMe);
  const collected = Boolean(row.collectedByMe);
  const likeCount = row.likeCount ?? 0;
  const collectCount = row.collectCount ?? 0;
  return (
    <div className="lf-vp-card-actions" aria-label="点赞与收藏">
      <button
        type="button"
        className="lf-vp-action-btn"
        onClick={(e) => void onToggleLike(e, row)}
        aria-label={liked ? "取消点赞" : "点赞"}
      >
        {liked ? (
          <LikeFilled className="lf-vp-action-ic lf-vp-action-ic--on" />
        ) : (
          <LikeOutlined className="lf-vp-action-ic" />
        )}
        <span className="lf-vp-action-num">{formatViewpointEngagementCount(likeCount)}</span>
      </button>
      <button
        type="button"
        className="lf-vp-action-btn"
        onClick={(e) => void onToggleCollect(e, row)}
        aria-label={collected ? "取消收藏" : "收藏"}
      >
        {collected ? (
          <StarFilled className="lf-vp-action-ic lf-vp-action-ic--collect-on" />
        ) : (
          <StarOutlined className="lf-vp-action-ic" />
        )}
        <span className="lf-vp-action-num">{formatViewpointEngagementCount(collectCount)}</span>
      </button>
    </div>
  );
}

function displayPublishedCompact(v: unknown): string {
  if (v == null || v === "") return "";
  if (v instanceof Date) return dayjs(v).format("M月D日 HH:mm");
  if (typeof v === "string") {
    const t = v.trim();
    const d = dayjs(t);
    return d.isValid() ? d.format("M月D日 HH:mm") : t;
  }
  return String(v).trim();
}

function nicknameOf(row: ViewpointFeedPost): string {
  const n = row.publisherNickname?.trim();
  if (n) return n;
  return row.publisherLabel?.trim() || "用户";
}

function refreshedAtLabel(t: Date | null): string {
  if (!t) return "—";
  return dayjs(t).format("M月D日 HH:mm");
}

type FeedListFilter = "all" | "liked" | "collected";

function importanceClass(imp: string): string {
  if (imp === "urgent") return "lf-vp-imp lf-vp-imp--urgent";
  if (imp === "important") return "lf-vp-imp lf-vp-imp--important";
  return "lf-vp-imp lf-vp-imp--normal";
}

function importanceLabel(imp: string): string {
  if (imp === "urgent") return "精选";
  if (imp === "important") return "重要";
  return "";
}

export interface ViewpointFeedPageProps {
  spaceId: string;
  spaceProductId: string;
  spaceDisplayName: string;
}

const ViewpointFeedPage: React.FC<ViewpointFeedPageProps> = observer(
  ({ spaceId, spaceProductId, spaceDisplayName: _spaceDisplayName }) => {
    const [pinnedItems, setPinnedItems] = useState<ViewpointFeedPost[]>([]);
    const [feedItems, setFeedItems] = useState<ViewpointFeedPost[]>([]);
    const [hasMoreOlder, setHasMoreOlder] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [feedFilter, setFeedFilter] = useState<FeedListFilter>("all");
    const [feedCategories, setFeedCategories] = useState<ViewpointFeedCategoryItem[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
    const [categoryMoreOpen, setCategoryMoreOpen] = useState(false);
    const [reportTarget, setReportTarget] = useState<ViewpointFeedPost | null>(null);
    const [reportOpen, setReportOpen] = useState(false);
    const [conceptCatalog, setConceptCatalog] = useState<ConceptBrief[]>([]);
    const pinnedRef = useRef<ViewpointFeedPost[]>([]);
    const feedRef = useRef<ViewpointFeedPost[]>([]);
    pinnedRef.current = pinnedItems;
    feedRef.current = feedItems;
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const { conceptEntitled, conceptModule } = useConceptEntitlement();
    const navigateToConcept = useConceptNavigate(spaceId);

    useEffect(() => {
      setSelectedCategoryId(null);
    }, [spaceId, spaceProductId]);

    useEffect(() => {
      void (async () => {
        const res = await ViewpointFeedApi.listViewpointCategories({ spaceId, spaceProductId });
        if (typeof res === "string") {
          setFeedCategories([]);
          return;
        }
        setFeedCategories(Array.isArray(res.list) ? res.list : []);
      })();
    }, [spaceId, spaceProductId]);

    useEffect(() => {
      const conceptSpaceProductId = conceptModule?.spaceProductId?.trim();
      if (!conceptSpaceProductId) {
        setConceptCatalog([]);
        return;
      }
      void (async () => {
        try {
          const list = await listViewpointConceptCatalog(conceptSpaceProductId);
          setConceptCatalog(Array.isArray(list) ? list : []);
        } catch {
          setConceptCatalog([]);
        }
      })();
    }, [conceptModule?.spaceProductId, spaceId]);

    const onConceptClick = useCallback(
      (concept: ConceptBrief) => {
        if (!conceptEntitled) {
          return;
        }
        navigateToConcept(concept.code);
      },
      [conceptEntitled, navigateToConcept]
    );

    const fetchPinnedRemote = useCallback(async () => {
      const res = await ViewpointFeedApi.getPinnedViewpoints({
        spaceId,
        spaceProductId,
        ...feedQueryExtras(selectedCategoryId),
      });
      if (typeof res === "string") {
        return [] as ViewpointFeedPost[];
      }
      return (res.list ?? []).map(normalizePinnedPost);
    }, [spaceId, spaceProductId, selectedCategoryId]);

    const reloadFirstPage = useCallback(async () => {
      setLoading(true);
      try {
        const extras = feedQueryExtras(selectedCategoryId);
        const [feedRes, nextPinned] = await Promise.all([
          ViewpointFeedApi.getOldViewpoints({
            spaceId,
            spaceProductId,
            limit: PAGE_SIZE,
            ...extras,
          }),
          fetchPinnedRemote(),
        ]);
        if (typeof feedRes === "string") {
          setPinnedItems([]);
          setFeedItems([]);
          setHasMoreOlder(false);
          return;
        }
        setPinnedItems(nextPinned);
        setFeedItems(feedRes.list.map(normalizeFeedOnlyPost));
        setHasMoreOlder(feedRes.hasMore);
        setRefreshedAt(new Date());
      } finally {
        setLoading(false);
      }
    }, [spaceId, spaceProductId, selectedCategoryId, fetchPinnedRemote]);

    const syncNewer = useCallback(async () => {
      const prevFeed = feedRef.current;
      const top = prevFeed[0];
      const extras = feedQueryExtras(selectedCategoryId);
      const [latestRes, nextPinned] = await Promise.all([
        top
          ? ViewpointFeedApi.getLatestViewpoints({
              spaceId,
              spaceProductId,
              limit: PAGE_SIZE,
              cursorPublishedAt: top.publishedAt,
              cursorId: top.id,
              ...extras,
            })
          : Promise.resolve({ list: [], hasMore: false }),
        fetchPinnedRemote(),
      ]);
      if (typeof latestRes !== "string" && latestRes.list.length) {
        setFeedItems((prev) =>
          mergeFeedIncoming(prev, latestRes.list.map(normalizeFeedOnlyPost))
        );
        setRefreshedAt(new Date());
      }
      setPinnedItems(nextPinned);
    }, [spaceId, spaceProductId, selectedCategoryId, fetchPinnedRemote]);

    const displayItems = useMemo(() => {
      const all = buildViewpointDisplayItems(pinnedItems, feedItems);
      if (feedFilter === "liked") {
        return all.filter((x) => Boolean(x.likedByMe));
      }
      if (feedFilter === "collected") {
        return all.filter((x) => Boolean(x.collectedByMe));
      }
      return all;
    }, [pinnedItems, feedItems, feedFilter]);

    useEffect(() => {
      void reloadFirstPage();
    }, [reloadFirstPage]);

    useEffect(() => {
      const el = sentinelRef.current;
      if (!el) return undefined;
      const ob = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry?.isIntersecting || loading || loadingMore) return;
          if (!hasMoreOlder) return;
          const last = feedRef.current[feedRef.current.length - 1];
          if (!last) return;
          setLoadingMore(true);
          void (async () => {
            try {
              const res = await ViewpointFeedApi.getOldViewpoints({
                spaceId,
                spaceProductId,
                limit: PAGE_SIZE,
                cursorPublishedAt: last.publishedAt,
                cursorId: last.id,
                ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
              });
              if (typeof res === "string") {
                return;
              }
              setHasMoreOlder(res.hasMore);
              setFeedItems((prev) => [...prev, ...res.list.map(normalizeFeedOnlyPost)]);
            } finally {
              setLoadingMore(false);
            }
          })();
        },
        { root: null, rootMargin: "120px", threshold: 0 }
      );
      ob.observe(el);
      return () => ob.disconnect();
    }, [
      loading,
      loadingMore,
      hasMoreOlder,
      spaceId,
      spaceProductId,
      selectedCategoryId,
      feedItems.length,
    ]);

    useEffect(() => {
      const id = window.setInterval(() => {
        void syncNewer();
      }, POLL_MS);
      return () => window.clearInterval(id);
    }, [syncNewer]);

    const patchEngagement = useCallback(
      (id: string, liked: boolean, collected: boolean) => {
        const applyPatch = (r: ViewpointFeedPost): ViewpointFeedPost => {
          if (r.id !== id) return r;
          let likeCount = r.likeCount ?? 0;
          let collectCount = r.collectCount ?? 0;
          const wasLiked = Boolean(r.likedByMe);
          const wasColl = Boolean(r.collectedByMe);
          if (liked !== wasLiked) {
            likeCount += liked ? 1 : -1;
          }
          if (collected !== wasColl) {
            collectCount += collected ? 1 : -1;
          }
          return {
            ...r,
            likedByMe: liked,
            collectedByMe: collected,
            likeCount: Math.max(0, likeCount),
            collectCount: Math.max(0, collectCount),
          };
        };
        setPinnedItems((prev) => prev.map(applyPatch));
        setFeedItems((prev) => prev.map(applyPatch));
      },
      []
    );

    const onToggleLike = useCallback(
      async (e: React.MouseEvent, row: ViewpointFeedPost) => {
        e.stopPropagation();
        const res = await ViewpointFeedApi.toggleLike({
          spaceId,
          spaceProductId,
          viewpointId: row.id,
        });
        if (typeof res === "string") {
          return;
        }
        patchEngagement(row.id, res.liked, res.collected);
      },
      [spaceId, spaceProductId, patchEngagement]
    );

    const onToggleCollect = useCallback(
      async (e: React.MouseEvent, row: ViewpointFeedPost) => {
        e.stopPropagation();
        const res = await ViewpointFeedApi.toggleCollect({
          spaceId,
          spaceProductId,
          viewpointId: row.id,
        });
        if (typeof res === "string") {
          return;
        }
        patchEngagement(row.id, res.liked, res.collected);
      },
      [spaceId, spaceProductId, patchEngagement]
    );

    const onReportClick = useCallback(
      (e: React.MouseEvent, row: ViewpointFeedPost) => {
        e.stopPropagation();
        if (!globalModel.isLoggedIn) {
          globalModel.openLoginModal();
          return;
        }
        setReportTarget(row);
        setReportOpen(true);
      },
      []
    );

    const closeReportModal = useCallback(() => {
      setReportOpen(false);
      setReportTarget(null);
    }, []);

    const showReportActions = globalModel.financeShareEnabled;

    const showEnd = !loading && feedItems.length > 0 && !hasMoreOlder;
    const showFilteredEmpty =
      !loading && (pinnedItems.length > 0 || feedItems.length > 0) && displayItems.length === 0;

    const onClickRefresh = useCallback(() => {
      void reloadFirstPage();
    }, [reloadFirstPage]);

    const onPickFeedCategory = useCallback(
      (categoryId: string) => {
        const id = categoryId.trim();
        if (!id) return;
        const nextId = selectedCategoryId === id ? null : id;
        setSelectedCategoryId(nextId);
        if (nextId !== null) {
          setFeedFilter("all");
        }
        setCategoryMoreOpen(false);
      },
      [selectedCategoryId]
    );

    const onSelectFeedCategory = useCallback((categoryId: string) => {
      const id = categoryId.trim();
      if (!id) return;
      setSelectedCategoryId(id);
      setFeedFilter("all");
    }, []);

    const onPickFeedFilter = useCallback((key: FeedListFilter) => {
      setFeedFilter(key);
      if (key === "all" || key === "liked" || key === "collected") {
        setSelectedCategoryId(null);
      }
    }, []);

    const validFeedCategories = useMemo(
      () =>
        feedCategories.filter((c) => {
          const nm = c.name?.trim();
          const sid = c.id?.trim();
          return Boolean(nm && sid);
        }),
      [feedCategories]
    );

    const inlineFeedCategories = useMemo(
      () => validFeedCategories.slice(0, FEED_CATEGORY_INLINE_MAX),
      [validFeedCategories]
    );

    const overflowFeedCategories = useMemo(() => {
      if (validFeedCategories.length <= FEED_CATEGORY_INLINE_MAX) return [];
      return validFeedCategories.slice(FEED_CATEGORY_INLINE_MAX);
    }, [validFeedCategories]);

    const selectedFeedCategoryIndex = useMemo(() => {
      if (!selectedCategoryId) return -1;
      const id = selectedCategoryId.trim();
      return validFeedCategories.findIndex((c) => c.id.trim() === id);
    }, [selectedCategoryId, validFeedCategories]);

    const feedCategoryMoreActive =
      overflowFeedCategories.length > 0 && selectedFeedCategoryIndex >= FEED_CATEGORY_INLINE_MAX;

    const feedCategoryMoreTriggerLabel = useMemo(() => {
      if (overflowFeedCategories.length === 0) return "更多分类";
      if (!feedCategoryMoreActive || selectedFeedCategoryIndex < 0) return "更多分类";
      return (
        validFeedCategories[selectedFeedCategoryIndex]?.name?.trim() || "更多分类"
      );
    }, [
      overflowFeedCategories.length,
      feedCategoryMoreActive,
      selectedFeedCategoryIndex,
      validFeedCategories,
    ]);

    return (
      <div className="lf-vp-feed">
        <div className="lf-vp-feed__inner">
          <div className="lf-vp-feed__toolbar" aria-label="列表筛选与刷新">
            <div className="lf-vp-feed__filters-row" role="tablist" aria-label="观点范围">
              <button
                type="button"
                role="tab"
                aria-selected={feedFilter === "all" && !selectedCategoryId}
                className={
                  feedFilter === "all" && !selectedCategoryId
                    ? "lf-vp-feed__filter lf-vp-feed__filter--on lf-vp-feed__filter--pinned"
                    : "lf-vp-feed__filter lf-vp-feed__filter--pinned"
                }
                onClick={() => onPickFeedFilter("all")}
              >
                全部
              </button>
              <div className="lf-vp-feed__filters-scroll">
                <div className="lf-vp-feed__filters-scroll-inner">
                  {(
                    [
                      ["liked", "我点赞的"],
                      ["collected", "我收藏的"],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={feedFilter === key}
                      className={
                        feedFilter === key
                          ? "lf-vp-feed__filter lf-vp-feed__filter--on"
                          : "lf-vp-feed__filter"
                      }
                      onClick={() => onPickFeedFilter(key)}
                    >
                      {label}
                    </button>
                  ))}
                  {inlineFeedCategories.map((c) => {
                    const nm = c.name!.trim();
                    const on = selectedCategoryId === c.id.trim();
                    return (
                      <button
                        key={c.id}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        className={
                          on ? "lf-vp-feed__filter lf-vp-feed__filter--on" : "lf-vp-feed__filter"
                        }
                        onClick={() => onPickFeedCategory(c.id)}
                      >
                        {nm}
                      </button>
                    );
                  })}
                  {overflowFeedCategories.length > 0 ? (
                    <Dropdown
                      trigger={["click"]}
                      placement="topLeft"
                      open={categoryMoreOpen}
                      onOpenChange={setCategoryMoreOpen}
                      popupRender={() => (
                        <div className="lf-vp-feed__filter-more-panel" role="listbox">
                          {overflowFeedCategories.map((c) => {
                            const nm = c.name!.trim();
                            const on = selectedCategoryId === c.id.trim();
                            return (
                              <button
                                key={c.id}
                                type="button"
                                role="option"
                                className={
                                  on
                                    ? "lf-vp-feed__filter-more-cell lf-vp-feed__filter-more-cell--on"
                                    : "lf-vp-feed__filter-more-cell"
                                }
                                onClick={() => onPickFeedCategory(c.id)}
                                title={nm}
                              >
                                <span className="lf-vp-feed__filter-more-cell-text">{nm}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      overlayStyle={{ padding: 0, background: "transparent", boxShadow: "none" }}
                    >
                      <span className="lf-vp-feed__filter-more-anchor">
                        <button
                          type="button"
                          className={
                            feedCategoryMoreActive
                              ? "lf-vp-feed__filter lf-vp-feed__filter--on lf-vp-feed__filter-more-trigger"
                              : "lf-vp-feed__filter lf-vp-feed__filter-more-trigger"
                          }
                          aria-expanded={categoryMoreOpen}
                          aria-haspopup="listbox"
                        >
                          <span className="lf-vp-feed__filter-more-label">
                            {feedCategoryMoreTriggerLabel}
                          </span>
                          <DownOutlined className="lf-vp-feed__filter-more-caret" />
                        </button>
                      </span>
                    </Dropdown>
                  ) : null}
                </div>
              </div>
            </div>
            <button
              type="button"
              className="lf-vp-feed__refresh"
              onClick={onClickRefresh}
              disabled={loading}
              aria-label="立即刷新列表"
            >
              更新于 {refreshedAtLabel(refreshedAt)}
            </button>
          </div>
          <Spin spinning={loading}>
            <div className="lf-vp-feed__waterfall">
              {showFilteredEmpty ? (
                <div className="lf-vp-feed__empty-filter">当前筛选下暂无内容</div>
              ) : null}
              {displayItems.map((row) => {
                const nick = nicknameOf(row);
                const titleText =
                  row.titleMode && row.title?.trim() ? row.title.trim() : null;
                const categoryName = row.categoryName?.trim() || "";
                const categoryId = row.categoryId?.trim() || "";
                const showImportance =
                  row.importance === "important" || row.importance === "urgent";
                return (
                  <article key={row.id} className="lf-vp-card">
                    <div className="lf-vp-card__head">
                      <EasyAvatar
                        shape="square"
                        squareRadius={4}
                        size={30}
                        url={resolveAppMediaUrl(row.publisherAvatarUrl) || undefined}
                        word={avatarDisplayWord(nick, row.publisherLabel)}
                        alt=""
                        className="lf-vp-card__avatar"
                      />
                      <div className="lf-vp-card__main">
                        <div className="lf-vp-card__user-line">
                          <span className="lf-vp-card__nick">{nick}</span>
                          <span className="lf-vp-card__time-inline">
                            {displayPublishedCompact(row.publishedAt)}
                          </span>
                          {row.pinned ? (
                            <span className="lf-vp-pinned-badge">置顶</span>
                          ) : null}
                          {categoryName && categoryId ? (
                            <button
                              type="button"
                              className="lf-vp-card__category-tag"
                              onClick={() => onSelectFeedCategory(categoryId)}
                            >
                              {categoryName}
                            </button>
                          ) : null}
                        </div>
                        {titleText ? (
                          <div className="lf-vp-card__title-text">{titleText}</div>
                        ) : null}
                        <ViewpointConceptLinkedBody
                          content={row.content}
                          catalog={conceptCatalog}
                          onConceptClick={onConceptClick}
                          importancePrefix={
                            showImportance ? (
                              <span className={importanceClass(row.importance)}>
                                {importanceLabel(row.importance)}
                              </span>
                            ) : null
                          }
                          hotBadge={<ViewpointHotBadge likeCount={row.likeCount ?? 0} />}
                        />
                        {row.imageUrls?.length ? (
                          <div className="lf-vp-card__images">
                            <Image.PreviewGroup preview={{ destroyOnClose: true }}>
                              <div className="lf-vp-card__images-inner">
                                {row.imageUrls.map((u, idx) => (
                                  <div key={`${idx}-${u}`} className="lf-vp-card__image-cell">
                                    <Image src={u} alt="" className="lf-vp-card__image" />
                                  </div>
                                ))}
                              </div>
                            </Image.PreviewGroup>
                          </div>
                        ) : null}
                      </div>
                      <ViewpointFeedEngagementActions
                        row={row}
                        onToggleLike={onToggleLike}
                        onToggleCollect={onToggleCollect}
                      />
                      <ViewpointConceptTagRow
                        content={row.content}
                        catalog={conceptCatalog}
                        onConceptClick={onConceptClick}
                        suffix={
                          showReportActions ? (
                            <ViewpointFeedReportAction row={row} onReport={onReportClick} />
                          ) : undefined
                        }
                      />
                    </div>
                  </article>
                );
              })}
              <div ref={sentinelRef} className="lf-vp-feed__sentinel" aria-hidden />
              {loadingMore ? (
                <div className="lf-vp-feed__loading-more">
                  <Spin size="small" /> 加载中…
                </div>
              ) : null}
              {showEnd ? <div className="lf-vp-feed__end">已经到底啦</div> : null}
            </div>
          </Spin>
        </div>
        <ViewpointReportModal
          open={reportOpen}
          row={reportTarget}
          spaceId={spaceId}
          spaceProductId={spaceProductId}
          onClose={closeReportModal}
        />
      </div>
    );
  }
);

export default ViewpointFeedPage;
